import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function AdminRolesPage() {
  const { user } = useAuth();
  const [caps, setCaps] = useState([]);
  const [roles, setRoles] = useState({});
  const [saving, setSaving] = useState(null);
  const isSuperAdmin = (user?.roles || []).includes("super_administrator");

  useEffect(() => {
    api.get("/admin/roles").then((r) => {
      setCaps(r.data.capabilities || []);
      setRoles(r.data.roles || {});
    });
  }, []);

  const toggle = (role, cap) => {
    setRoles((prev) => {
      const set = new Set(prev[role] || []);
      if (set.has(cap)) set.delete(cap); else set.add(cap);
      return { ...prev, [role]: Array.from(set) };
    });
  };

  const save = async (role) => {
    setSaving(role);
    try {
      await api.put("/admin/roles", { role, capabilities: roles[role] || [] });
      toast.success(`${role} permissions updated`);
    } catch (err) { toast.error(err?.response?.data?.error || "Save failed"); } finally { setSaving(null); }
  };

  return (
    <div className="max-w-4xl" data-testid="admin-roles">
      <div className="mb-6">
        <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Admin · Roles</div>
        <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#071b22]">Roles & permissions</h1>
        <p className="text-[#252A34]/70 text-sm mt-1">{isSuperAdmin ? "Toggle capabilities per role and save." : "Read-only — super-admins only can edit."}</p>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-[#252A34]/60 bg-slate-50">
              <th className="px-3 py-2">Role</th>
              {caps.map((c) => <th key={c} className="px-3 py-2 text-center whitespace-nowrap">{c.replace(/_/g, " ")}</th>)}
              {isSuperAdmin && <th className="px-3 py-2"></th>}
            </tr>
          </thead>
          <tbody>
            {Object.keys(roles).map((role) => (
              <tr key={role} className="border-t border-slate-100" data-testid={`role-row-${role}`}>
                <td className="px-3 py-2 font-medium">{role}</td>
                {caps.map((c) => (
                  <td key={c} className="px-3 py-2 text-center">
                    <input type="checkbox" disabled={!isSuperAdmin || role === "super_administrator"} checked={(roles[role] || []).includes(c)} onChange={() => toggle(role, c)} data-testid={`role-${role}-${c}`} />
                  </td>
                ))}
                {isSuperAdmin && (
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => save(role)} disabled={saving === role || role === "super_administrator"} className="inline-flex items-center gap-1 rounded-full bg-[#165a71] text-white px-3 py-1.5 text-xs disabled:opacity-50" data-testid={`role-save-${role}`}>
                      <Save className="h-3 w-3" /> {saving === role ? "…" : "Save"}
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
